import React from 'react';
import { useState, useEffect } from 'react';
import NavBar from '../layout/NavBar';
import ReactTable from 'react-table';


import { fetchEmergencyCallsAgents, updateEmergencyCallPrioritiesBatch } from '../../services/api/offline-consult';

const EmergencyCallsActivity = () => {
    const [dataSource, setDataSource] = useState([]);
    const [loading, setLoading] = useState(true);
    const [priorityUpdates, setPriorityUpdates] = useState({});
    const [activeUpdates, setActiveUpdates] = useState({});
    const [shiftTimeUpdates, setShiftTimeUpdates] = useState({});


    const handlePriorityChange = (agentId, rawValue) => {
        if (rawValue === '') {
            // allow clearing the input
            setPriorityUpdates((prev) => ({
                ...prev,
                [agentId]: null
            }));
            return;
        }

        const parsed = parseInt(rawValue, 10);

        // only allow numbers >= 1
        const newValue = parsed >= 1 ? parsed : null;

        setPriorityUpdates((prev) => ({
            ...prev,
            [agentId]: newValue
        }));
    };



    const handleActiveToggle = (agentId, checked) => {
        setActiveUpdates((prev) => ({
            ...prev,
            [agentId]: checked ? 1 : 0
        }));
    };

    const handleStartTimeChange = (agentId, value) => {
        setShiftTimeUpdates((prev) => ({
            ...prev,
            [agentId]: {
                ...prev[agentId],
                startTime: value,
            },
        }));
    };

    const handleEndTimeChange = (agentId, value) => {
        setShiftTimeUpdates((prev) => ({
            ...prev,
            [agentId]: {
                ...prev[agentId],
                endTime: value,
            },
        }));
    };



    const handleSave = async () => {
        const updates = [];


        const agentsToUpdate = new Set([
            ...Object.keys(priorityUpdates),
            ...Object.keys(activeUpdates),
            ...Object.keys(shiftTimeUpdates),
        ]);

        agentsToUpdate.forEach((agentId) => {
            const updatePayload = {
                agentId,

                ...(priorityUpdates[agentId] !== undefined && { priority: priorityUpdates[agentId] }),
                ...(activeUpdates[agentId] !== undefined && { active: activeUpdates[agentId] }),
                ...(shiftTimeUpdates[agentId]?.startTime !== undefined && { startTime: shiftTimeUpdates[agentId].startTime }),
                ...(shiftTimeUpdates[agentId]?.endTime !== undefined && { endTime: shiftTimeUpdates[agentId].endTime }),
            };


            if (Object.keys(updatePayload).length > 1) {
                updates.push(updatePayload);
            }
        });

        if (updates.length === 0) {
            alert("No changes to update.");
            return;
        }


        const existingPriorities = new Map(
            dataSource.map((agent) => [agent.agentId, agent.priority])
        );

        for (const update of updates) {
            if (update.priority !== undefined) {
                const priorityValue = update.priority;
                const currentAgentId = String(update.agentId);


                // for (const [otherAgentId, otherPriority] of existingPriorities.entries()) {
                //     const hasNewPriority = priorityUpdates[otherAgentId] !== undefined;
                //     const priorityToCheck = hasNewPriority ? priorityUpdates[otherAgentId] : otherPriority;

                //     if (currentAgentId !== String(otherAgentId) && priorityValue === priorityToCheck) {
                //         alert(
                //             `Priority ${priorityValue} is already assigned to another agent. Please choose a unique value.`
                //         );
                //         return;
                //     }
                // }


                for (const otherUpdate of updates) {
                    if (currentAgentId !== String(otherUpdate.agentId) && otherUpdate.priority === priorityValue) {
                        alert(
                            `Priority ${priorityValue} is already assigned to another agent in this batch. Please choose a unique value.`
                        );
                        return;
                    }
                }
            }
        }


        try {
            setLoading(true);


            await updateEmergencyCallPrioritiesBatch(updates);

            alert("Updates saved successfully!");

            // Refresh table
            const response = await fetchEmergencyCallsAgents();
            if (response && response.data) {
                setDataSource(response.data);
                setPriorityUpdates({});
                setActiveUpdates({});
                setShiftTimeUpdates({});
            }
        } catch (error) {
            console.error("Failed to save updates:", error);
            alert("Failed to save updates. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    // --- ReactTable Columns ---

    const columns = [
        {
            Header: 'Agent Name',
            accessor: 'agentName',
            width: 200,
        },
        {
            Header: 'Shift Start Time',
            accessor: 'startTime',
            Cell: ({ original }) => {
                const currentValue = shiftTimeUpdates[original.agentId]?.startTime ?? original.startTime;
                return (
                    <input
                        type="time"
                        value={currentValue || ''}
                        onChange={(e) => handleStartTimeChange(original.agentId, e.target.value)}
                        style={{ width: "100%", textAlign: "center" }}
                    />
                );
            },
            width: 200,
        },
        {
            Header: 'Shift End Time',
            accessor: 'endTime',
            Cell: ({ original }) => {
                const currentValue = shiftTimeUpdates[original.agentId]?.endTime ?? original.endTime;
                return (
                    <input
                        type="time"
                        value={currentValue || ''}
                        onChange={(e) => handleEndTimeChange(original.agentId, e.target.value)}
                        style={{ width: "100%", textAlign: "center" }}
                    />
                );
            },
            width: 200,
        },
        {
            Header: 'Active',
            accessor: 'active',
            Cell: ({ original }) => {
                const currentValue =
                    activeUpdates[original.agentId] !== undefined
                        ? activeUpdates[original.agentId]
                        : original.active;

                return (
                    <div style={{ display: 'flex', justifyContent: 'center' }}>
                        <label style={{ position: 'relative', display: 'inline-block', width: '50px', height: '28px' }}>
                            <input
                                type="checkbox"
                                checked={currentValue === 1}
                                onChange={(e) => handleActiveToggle(original.agentId, e.target.checked)}
                                style={{
                                    opacity: 0,
                                    width: 0,
                                    height: 0,
                                    position: 'absolute',
                                }}
                            />
                            <span
                                style={{
                                    position: 'absolute',
                                    cursor: 'pointer',
                                    top: 0,
                                    left: 0,
                                    right: 0,
                                    bottom: 0,
                                    backgroundColor: currentValue === 1 ? '#714fff' : '#ccc',
                                    transition: '0.4s',
                                    borderRadius: '34px',
                                }}
                            >
                                <span
                                    style={{
                                        position: 'absolute',
                                        height: '22px',
                                        width: '22px',
                                        left: currentValue === 1 ? '25px' : '3px',
                                        bottom: '3px',
                                        backgroundColor: 'white',
                                        transition: '0.4s',
                                        borderRadius: '50%',
                                    }}
                                ></span>
                            </span>
                        </label>
                    </div>
                );
            },
            style: { textAlign: 'center' },
            width: 200,
        },
        {
            Header: 'Priority',
            accessor: 'priority',
            style: { textAlign: 'center' },
            width: 200,
        },
        {
            Header: 'Update Priority',
            accessor: 'updatePriority',
            Cell: ({ original }) => (
                <input
                    type="number"
                    min="1"
                    value={
                        priorityUpdates[original.agentId] !== undefined
                            ? priorityUpdates[original.agentId] ?? '' // allow empty string if null
                            : original.priority ?? ''
                    }
                    placeholder="Enter new priority"
                    onChange={(e) => handlePriorityChange(original.agentId, e.target.value)}
                    style={{ width: "100%", textAlign: "center" }}
                />


            ),
            width: 200,
        }
    ];

    const tableStyle = {
        border: 'none',
        fontSize: '15px',
        backgroundColor: '#FFFFFF',
        fontWeight: '500',
        fontFamily: 'Montserrat',
        padding: '10px',
    };

    useEffect(() => {
        const loadEmergencyCalls = async () => {
            try {
                setLoading(true);
                const response = await fetchEmergencyCallsAgents();
                if (response && response.data) {
                    setDataSource(response.data);
                }
            } catch (error) {
                console.error("Failed to fetch emergency calls data:", error);
            } finally {
                setLoading(false);
            }
        };

        loadEmergencyCalls();
    }, []);

    return (
        <div>
            <div>
                <NavBar />
            </div>
            <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                flexDirection: 'column',
                marginTop: '72px',
                marginLeft: '20px',
            }}>
                <div>
                    <h1>Emergency Calls Activity</h1>
                </div>
                <div>
                    <ReactTable
                        style={tableStyle}
                        showPaginationTop
                        data={dataSource}
                        columns={columns}
                        loading={loading}
                        defaultPageSize={10}
                    />
                </div>
                <div style={{ marginTop: "20px", textAlign: "right", marginRight: "20px" }}>
                    <button
                        onClick={handleSave}
                        style={{
                            backgroundColor: "#007bff",
                            color: "#fff",
                            border: "none",
                            padding: "10px 20px",
                            borderRadius: "5px",
                            cursor: "pointer",
                            fontSize: "16px"
                        }}
                        disabled={loading}
                    >
                        Save
                    </button>
                </div>
            </div>
        </div>
    );
};

export default EmergencyCallsActivity;